import React, {ChangeEvent, ReactElement, ReactNode} from 'react'
import {createResidentPage, ResidentPagePropsSet} from '../shared/hoc/createResidentPage'
import {StyledPageTitle} from '../reservations/components/CommonStyle'
import {
    ItemHeading,
    WideWhiteArea
} from '../../__NoAutomatedTests/components/applications/common/resident/NatResidentApplicationsCommon'
import {SessionStorageHelper} from '../../SessionStorageHelper'
import PPStepper from '../shared/PPStepper'
import {currentMedia, HideInPC, HideInSP} from '../../styledComponents/ResponsiveConvenience'
import PPRadioGroupForResident, {ResidentRadioButtonProps} from '../shared/forms/resident/PPRadioGroupForResident'
import {HAVE_OR_NOT, MESSAGE_TYPE} from '../../CodeMaster'
import {PrimaryStyledButton, StyledSecondaryButton} from '../../styledComponents/Button'
import MessageDialogComponent from '../../common/resident/MessageDialogComponent'
import {Messages} from '../../Messages'
import {DisplayItemDividerForPageTop, DisplayItemInputTitleRequired} from '../../styledComponents/DisplayItem'
import styled from 'styled-components'
import ResidentGeneralMeetingRepo from './ResidentGeneralMeetingRepo'
import {NatAsyncCallFunction} from '../../__NoAutomatedTests/common/util/Natutil'
import {
    unitOwnerConfirmationStepsForPc,
    unitOwnerConfirmationStepsForSp
} from './ResidentUnitOwnerConfirmationVerificationMethodSelectionPage'
import {PlainBackLink} from '../../styledComponents/Links'
import {Location} from 'react-router-dom'
import {Box} from '@mui/material'

export interface ResidentUnitOwnerConfirmationLocationState {
    unitOwnerConfirmationKey: string
}

interface Props {
    location: Location<ResidentUnitOwnerConfirmationLocationState>
    residentGeneralMeetingRepo: ResidentGeneralMeetingRepo
}

interface State {
    unitOwnership: string
    isOpenUnitOwnershipConfirmationDialog: boolean
    dialogErrorLevel: MESSAGE_TYPE
    dialogMessage: string
}

class ResidentUnitOwnerConfirmationPage extends React.Component<Props & ResidentPagePropsSet, State> {
    constructor(props: Props & ResidentPagePropsSet) {
        super(props)
        if (!props.location.state.unitOwnerConfirmationKey) {
            throw new Error('区分所有者の確認用キーがありません。画面遷移が不正です。')
        }

        this.state = {
            unitOwnership: '',
            isOpenUnitOwnershipConfirmationDialog: false,
            dialogErrorLevel: MESSAGE_TYPE.NOTHING,
            dialogMessage: '',
        }
        this.handleChangeUnitOwnership = this.handleChangeUnitOwnership.bind(this)
        this.handleCompleteButtonClick = this.handleCompleteButtonClick.bind(this)
        this.handleCancelButtonClick = this.handleCancelButtonClick.bind(this)
        this.handleDialogChangeButtonClick = this.handleDialogChangeButtonClick.bind(this)
        this.handleDialogCancelButtonClick = this.handleDialogCancelButtonClick.bind(this)
    }

    handleChangeUnitOwnership(event: ChangeEvent<HTMLInputElement>): void {
        this.setState({
            unitOwnership: event.target.value
        })
    }

    async handleCompleteButtonClick(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const unitOwnerResponse = await NatAsyncCallFunction(
                this.props.residentGeneralMeetingRepo.getUnitOwnerByLoginUserRoom.bind(
                    this.props.residentGeneralMeetingRepo,
                ),
                this.props.navigate,
            )

            if (!unitOwnerResponse) {
                return
            }

            const unitOwner = unitOwnerResponse.details
            const loginUserId = SessionStorageHelper.getStringItemOrNull('userId')
            if (!loginUserId) {
                throw new Error('ユーザIDの取得に失敗しました。')
            }

            if (unitOwner) {
                if (unitOwner.userId !== loginUserId && HAVE_OR_NOT.HAVE.value === this.state.unitOwnership) {
                    const unitOwnerName = unitOwner.name !== '' ? unitOwner.name + 'さん' : '他ユーザ'
                    this.setState({
                        isOpenUnitOwnershipConfirmationDialog: true,
                        dialogErrorLevel: Messages.WGMTR0001.errorLevel,
                        dialogMessage: Messages.WGMTR0001.toString(unitOwnerName),
                    })
                    return
                }
                if (unitOwner.userId === loginUserId && HAVE_OR_NOT.NOT.value === this.state.unitOwnership) {
                    this.setState({
                        isOpenUnitOwnershipConfirmationDialog: true,
                        dialogErrorLevel: Messages.WGMTR0002.errorLevel,
                        dialogMessage: Messages.WGMTR0002.toString(),
                    })
                    return
                }
            }
            await this.updateUnitOwnership()
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    async updateUnitOwnership(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            await NatAsyncCallFunction(
                this.props.residentGeneralMeetingRepo.updateUnitOwnershipByLoginUserRoom.bind(
                    this.props.residentGeneralMeetingRepo,
                    this.props.location.state.unitOwnerConfirmationKey,
                    HAVE_OR_NOT.codeToEnumConstants(this.state.unitOwnership),
                ),
                this.props.navigate,
            )

            this.props.navigate(
                '/resident/general-meeting/unit-owner-confirmation-complete',
                {
                    state: {
                        unitOwnership: HAVE_OR_NOT.codeToEnumConstants(
                            this.state.unitOwnership
                        ),
                    },
                }
            )
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    async handleDialogChangeButtonClick(): Promise<void> {
        await this.updateUnitOwnership()
    }

    handleDialogCancelButtonClick(): void {
        this.setState({
            isOpenUnitOwnershipConfirmationDialog: false,
            dialogErrorLevel: MESSAGE_TYPE.NOTHING,
            dialogMessage: '',
        })
    }

    handleCancelButtonClick(): void {
        this.props.navigate('/resident/mypage')
    }

    getHaveOrNotOptions(): ResidentRadioButtonProps[] {
        return HAVE_OR_NOT.values().reverse().map((haveOrNot) => {
            let label
            if (haveOrNot === HAVE_OR_NOT.HAVE) {
                label = 'はい（区分所有者です）'
            } else {
                label = 'いいえ（区分所有者ではありません）'
            }
            return {
                label: label,
                value: haveOrNot.value
            }
        })
    }

    renderUnitOwnershipConfirmationDialog(): ReactElement {
        return <MessageDialogComponent
            open={this.state.isOpenUnitOwnershipConfirmationDialog}
            params={{
                title: '確認',
                errorLevel: this.state.dialogErrorLevel,
                message: this.state.dialogMessage,
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: this.handleDialogCancelButtonClick,
                    },
                    {
                        isDefault: false,
                        buttonLabel: '変更する',
                        buttonHandler: this.handleDialogChangeButtonClick,
                    },
                ],
            }}
        />
    }

    render(): ReactNode {
        const condominiumName = SessionStorageHelper.getStringItemOrNull('selectedCondominiumName')
        const ridgeName = SessionStorageHelper.getStringItemOrNull('selectedRidgeName')
        const roomNumber = SessionStorageHelper.getStringItemOrNull('selectedRoomNumber')
        return <>
            <ContentArea>
                <PlainBackLink
                    to={'/resident/mypage'}>
                    戻る
                </PlainBackLink>
                <StyledDisplayItemDividerForPageTop/>
                <HideInSP>
                    <PPStepper steps={unitOwnerConfirmationStepsForPc} activeStep={1}/>
                </HideInSP>
                <HideInPC>
                    <PPStepper steps={unitOwnerConfirmationStepsForSp} activeStep={1} alternativeLabel/>
                </HideInPC>
                <UnitOwnerStyledPageTitle>区分所有者の確認</UnitOwnerStyledPageTitle>
                <DetailMessage>
                    <div>管理組合の総会では、区分所有者ご本人のみが議決権を行使することができます。</div>
                    <div>ご自身が対象住戸の区分所有者であることを確認し登録してください。</div>
                </DetailMessage>
                <UnitOwnerItemHeading>
                    対象住戸
                </UnitOwnerItemHeading>
                <UnitOwnerWideWhiteArea>
                    {`${condominiumName}　${ridgeName} ${roomNumber}`}
                </UnitOwnerWideWhiteArea>
                <UnitOwnerItemHeading>
                    区分所有者の確認
                </UnitOwnerItemHeading>
                <UnitOwnerWideWhiteArea>
                    <div>あなたは対象物件の区分所有者ご本人ですか？</div>
                    <UnitOwnerDisplayItemInputTitleRequired>区分所有者</UnitOwnerDisplayItemInputTitleRequired>
                    <PPRadioGroupForResident
                        data={this.getHaveOrNotOptions()}
                        selectValue={this.state.unitOwnership}
                        onChange={this.handleChangeUnitOwnership}
                        eventKeys={['unitOwnership']}
                        eventEmitter={this.props.eventEmitter}/>
                </UnitOwnerWideWhiteArea>
                <CompleteButton
                    disabled={this.state.unitOwnership === ''}
                    onClick={this.handleCompleteButtonClick}>
                    完了する
                </CompleteButton>
                <CanselButton
                    onClick={this.handleCancelButtonClick}>
                    キャンセル
                </CanselButton>
                <Box height={'15px'}/>
            </ContentArea>
            {this.renderUnitOwnershipConfirmationDialog()}
        </>
    }
}

export default createResidentPage(ResidentUnitOwnerConfirmationPage)

const ContentArea = styled.div`
  padding: 0 0 15px 0;
`

const StyledDisplayItemDividerForPageTop = styled(DisplayItemDividerForPageTop)`
  margin-top: 14px;
  margin-bottom: 15px;
`

const UnitOwnerStyledPageTitle = styled(StyledPageTitle)`
  margin-top: 30px;
`

const DetailMessage = styled.div`
  margin-top: 20px;
  margin-bottom: 40px;
  ${currentMedia.lessThan('sp')`
        margin-bottom: 20px;
    `}
`

const UnitOwnerItemHeading = styled(ItemHeading)`
  margin-top: 15px;
  padding: 6px 15px;
`

const UnitOwnerWideWhiteArea = styled(WideWhiteArea)`
  padding: 20px 15px;
`

const UnitOwnerDisplayItemInputTitleRequired = styled(DisplayItemInputTitleRequired)`
  margin-top: 10px;
`

const CompleteButton = styled(PrimaryStyledButton)`
  margin-top: 20px;
`

const CanselButton = styled(StyledSecondaryButton)`
  margin-top: 30px;
`
